import React, { ComponentType } from 'react';
import { StyleSheet } from 'react-native';
import {
  CellProps,
  isIntegerControl,
  JsonFormsCellRendererRegistryEntry,
  RankedTester,
  rankWith,
  and,
  optionIs,
  schemaMatches,
} from '@jsonforms/core';
import { withJsonFormsCellProps } from '@jsonforms/react';
import { FloatingLabelInput } from 'react-native-floating-label-input';
import Colors from '../../../../constants/Colors';
import { RENDERER_RANK_PREFERRED_MATCH } from '../constants';

const styles = StyleSheet.create({
  inputContainer: {
    borderWidth: 0,
    borderBottomRightRadius: 0,
    borderBottomLeftRadius: 0,
    width: 100,
    overflow: 'hidden',
    marginTop: 8,
  },
  input: {
    color: Colors.veryDarkGray,
    fontSize: 16,
    fontFamily: 'roboto-regular',
    borderBottomWidth: 1,
    paddingBottom: 4,
    borderBottomColor: Colors.grayBorder,
  },
  errorInput: {
    borderBottomColor: Colors.brightRed,
  },
});

/**
 * Tester to determine if a control can be used with this cell.
 * Matches integer controls with format option set to "long" and minimum >= 0.
 */
const tester: RankedTester = rankWith(
  RENDERER_RANK_PREFERRED_MATCH + 1, // Higher than RENDERER_RANK_PREFERRED_MATCH to override IntegerCell
  and(
    isIntegerControl,
    optionIs('format', 'long'),
    schemaMatches((schema) => schema.minimum !== undefined && schema.minimum >= 0)
  )
);

/**
 * Cell renderer for long positive integers using text input with numeric keyboard.
 * Better UX than plus/minus buttons for large integer values.
 * Only accepts positive integers (0 or greater).
 * @param props Control props to be hydrated using `withJsonFormsCellProps`.
 */
const LongPositiveIntegerCellRenderer: ComponentType<CellProps> = (props: CellProps) => {
  const handleChangeText = (text: string) => {
    // Allow empty string
    if (text === '') {
      props.handleChange(props.path, undefined);
      return;
    }

    // Only allow valid positive integer input (no negative sign)
    // Cap at MAX_SAFE_INTEGER to prevent precision loss
    const numericValue = parseInt(text, 10);
    if (!Number.isNaN(numericValue) && numericValue >= 0 && numericValue <= Number.MAX_SAFE_INTEGER) {
      props.handleChange(props.path, numericValue);
    }
  };

  return (
    <FloatingLabelInput
      testID={`LongPositiveIntegerCell-${props.id}`}
      label={
        // don't float the label, instead just show a static label
        ''
      }
      containerStyles={styles.inputContainer}
      inputStyles={{
        ...styles.input,
        ...(props.errors.length > 0 ? styles.errorInput : {}),
      }}
      keyboardType="numeric"
      onChangeText={handleChangeText}
      value={props.data?.toString() ?? ''}
    />
  );
};

/**
 * Renderer registry entry for long positive integer input control.
 * Bundles the tester and the wrapped control for use with form.
 */
// eslint-disable-next-line import/prefer-default-export
export const LongPositiveIntegerCell: JsonFormsCellRendererRegistryEntry = {
  tester,
  cell: withJsonFormsCellProps(LongPositiveIntegerCellRenderer),
};
